import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, Table, Badge, Button, Spinner } from 'react-bootstrap';
import { FiPlus, FiEdit2, FiToggleLeft, FiToggleRight, FiTrash2, FiMail, FiPhone } from 'react-icons/fi';
import { toast } from 'react-toastify';
import adminService, { User } from '../../services/admin.service';

const UsersList: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const data = await adminService.listUsers();
      setUsers(data);
    } catch (error) {
      toast.error('Error al cargar los usuarios');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async (id: number) => {
    try {
      await adminService.toggleUserStatus(id);
      toast.success('Estado actualizado');
      loadUsers();
    } catch (error) {
      toast.error('Error al cambiar el estado');
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('¿Desactivar este usuario?')) {
      try {
        await adminService.deleteUser(id);
        toast.success('Usuario desactivado');
        loadUsers();
      } catch (error) {
        toast.error('Error al desactivar el usuario');
      }
    }
  };

  const getRoleBadge = (roleName?: string, roleCode?: string) => {
    const variants: Record<string, string> = {
      'super_admin': 'danger',
      'coordinador': 'warning',
      'abogado': 'primary',
      'auxiliar': 'info'
    };
    return <Badge bg={variants[roleCode || ''] || 'secondary'}>{roleName || 'Sin rol'}</Badge>;
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('es-CO');
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1 className="h3 mb-0">Usuarios</h1>
        <Link to="/users/new" className="btn btn-primary">
          <FiPlus className="me-2" /> Nuevo Usuario
        </Link>
      </div>

      <Card>
        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : users.length === 0 ? (
            <div className="text-center py-5 text-muted">
              No hay usuarios registrados
            </div>
          ) : (
            <Table hover responsive className="mb-0">
              <thead>
                <tr>
                  <th>Nombre</th>
                  <th>Email</th>
                  <th>Teléfono</th>
                  <th>Rol</th>
                  <th>Estado</th>
                  <th>Creado</th>
                  <th style={{ width: '120px' }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id} className={!user.is_active ? 'table-secondary' : ''}>
                    <td>
                      <strong>{user.full_name}</strong>
                    </td>
                    <td>
                      <a href={`mailto:${user.email}`}>
                        <FiMail className="me-1" /> {user.email}
                      </a>
                    </td>
                    <td>
                      {user.phone ? (
                        <span><FiPhone className="me-1" /> {user.phone}</span>
                      ) : '-'}
                    </td>
                    <td>{getRoleBadge(user.role_name, user.role_code)}</td>
                    <td>
                      <Badge bg={user.is_active ? 'success' : 'secondary'}>
                        {user.is_active ? 'Activo' : 'Inactivo'}
                      </Badge>
                    </td>
                    <td>{formatDate(user.created_at)}</td>
                    <td>
                      <Link to={`/users/${user.id}/edit`} className="btn btn-outline-primary btn-sm me-1" title="Editar">
                        <FiEdit2 />
                      </Link>
                      <Button
                        variant={user.is_active ? 'outline-warning' : 'outline-success'}
                        size="sm"
                        className="me-1"
                        title={user.is_active ? 'Desactivar' : 'Activar'}
                        onClick={() => handleToggleStatus(user.id)}
                      >
                        {user.is_active ? <FiToggleRight /> : <FiToggleLeft />}
                      </Button>
                      {!user.is_active && (
                        <Button
                          variant="outline-danger"
                          size="sm"
                          title="Eliminar"
                          onClick={() => handleDelete(user.id)}
                        >
                          <FiTrash2 />
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default UsersList;
